import type { Object3D } from 'three';
import type { AudioManager, PlayAt } from './audioManager';
import type { SoundPlacement, Variation, Mod, Drop } from './soundscape';
import { eraWeight } from './soundscape';
import type { AudioCue } from './cues';
import type { StateName } from '../state/types';
import { devBus } from '../debug/devBus';

// Single per-frame reconciler for the SCHEDULED soundscape — beds, the music
// slot, and one-shot cues. Replaces buildStateRules (per-era stateEnter rules)
// + CueScheduler, and owns the ambient/music channels so the schedule and the
// cassette override can never race:
//
//  - BEDS are level-triggered: a bed is "on" whenever the playhead sits inside
//    its day-window AND its era weighs in, with gain = base × eraWeight, so the
//    soundscape CROSSFADES continuously across a morph (past beds fade out as
//    present beds fade in). Scrubbing time/era just re-evaluates them.
//  - The MUSIC slot is diegetic from the boombox: entering an era loads that
//    era's default tape (unless a user tape is active); a cassette override
//    takes over and RIDES across era changes until ejected; ejecting goes
//    silent (no auto-resume) until the next tape or era change.
//  - ONE-SHOTS keep the edge-fired, scrub-safe, era-gated crossing logic and
//    fire via the injected callback (so they still run the full action
//    vocabulary). Transient events, so era gating stays binary.
//
// Gated on a running AudioContext (first gesture): before that the schedule is
// visible on the timeline but silent.

const MAX_STEP = 0.25;     // one-shot scrub guard: bigger jumps are seeks, not ticks
const ERA_EPSILON = 0.02;  // below this weight a bed counts as off
const BED_FADE = 0.5;
// A2 — resolveAt returns this when an AUTHORED hero anchor can't be resolved
// (renamed/missing hero). Callers SKIP rather than fall through to a global,
// unattenuated, room-filling loop. Distinct from `undefined` (no anchor = the
// placement is intentionally 'global').
const UNRESOLVED = Symbol('unresolved-anchor');
// N7 — how far the visitor's music channel dips while a ducking clip sounds
// (0.22 = down to 22% ≈ −13 dB). Deepened ~4 dB from 0.35 so Annour's narration
// clearly cuts through the cassette music (Bentley, 2026-06-29). John's ear tunes.
const N7_DUCK = 0.22;
// N7b — a narration line ALSO pulls the room (the ambient bed bus) back, not just
// the music. Ducking music alone left the wall narration buried under the full
// room whenever no cassette was playing (John, 2026-06-29: "the wall narration
// doesn't dip the audio like the others"). Gentler than the music duck — the room
// recedes under the voice, it doesn't vanish. John's ear tunes.
const N7_DUCK_AMBIENT = 0.5;
// N1 clips — starting values (named so retuning is an edit, not a hunt):
const CLIP_FADE = 0.02;       // default declick on region entry/exit/pause (s)
// W1 — the default audible fade applied to BOTH ends of a clip's envelope when
// it isn't given its own fadeIn/fadeOut, so nothing un-authored hard-clicks. A
// real fade, not the CLIP_FADE declick. John tunes by ear.
const DEFAULT_FADE = 0.06;
const CLIP_RESYNC_EPS = 0.25; // source-position drift (s) beyond which we re-seek
// A10 — below this absolute gain delta, the per-frame bed write is skipped (the
// target hasn't audibly moved). Small enough that a slow LFO still steps through.
const BED_GAIN_EPS = 0.01;

/** A10 — a bed's audible route, compared field-wise each frame (a live edit to
 *  any field re-routes the bed). Stored, not rebuilt as a string per frame. */
interface BedRoute { ch: string; spatial: string; ref?: number; max?: number; }

export interface SoundscapeDeps {
  audio: AudioManager;
  getPlacements: () => SoundPlacement[];
  getCues: () => AudioCue[];
  getObjectByHeroId: (id: string) => Object3D | undefined;
  currentEra: () => StateName;
  /** Morph progress 0 = past, 1 = present (drives the bed crossfade). */
  morph: () => number;
  /** Dispatch a fired one-shot (engine.run(cue.actions) + cue:fire). */
  fireCue: (cue: AudioCue) => void;
  /** Real seconds in one day-unit of clock t (N1 clips map day-t ↔ seconds). */
  dayLengthSeconds: () => number;
  /** E3 — beat 6, structure B's heartbeat: a visitor's tape finished NATURALLY
   *  (not ejected, not era-swapped). The app returns the room to the present —
   *  no button did this; time did. */
  onTapeEnded?: () => void;
  /** The generator gate (N8): placements marked `requiresPower` are inert
   *  until this reads true. Absent → always powered (gate not wired). */
  powered?: () => boolean;
  /** Per-bed runtime gate: when present and it returns false for a bed's id, that
   *  bed is held off (and fades out if playing) until it reads true again — same
   *  skip-before-live.add path as `requiresPower`. Used for the sewing-machine
   *  hum, which is OFF until the visitor clicks the machine (John, 2026-06-29:
   *  directors heard it run non-stop). Absent → every bed enabled. */
  bedEnabled?: (id: string) => boolean;
  /** Demo gate (silence-until-cassette): when this returns false the era's
   *  DEFAULT music never auto-plays — music is summoned ONLY by inserting a
   *  cassette (setMusicOverride). The cassette-studio metaphor: tapes are the
   *  music; the room is otherwise just beds + ambience. Absent → true (era
   *  defaults play — the authoring default, so the dev timeline's music region
   *  behaves as before). */
  autoEraMusic?: () => boolean;
}

export class SoundscapeEngine {
  private lastT = -1;
  private bedHandles = new Map<string, string>(); // placementId -> audio handle
  // Beds whose chance-roll failed for the CURRENT activation; cleared when the
  // bed leaves its window so the next entry re-rolls. Without this the roll
  // would re-fire every frame and eventually always pass.
  private bedSkipped = new Set<string>();
  // audioTime each bed (re)started — drives reshuffle (loop-to-loop) timing.
  private bedStarted = new Map<string, number>();
  // Route each bed handle was started with (channel + spatial falloff). A live
  // edit to any field re-routes the bed (stop + restart) so channel changes AND
  // spatial-radius tuning take effect on the audible sound, not just the viz.
  // A10: stored field-wise (not a per-frame template string — pure allocation);
  // NOT hoisted out of the loop because these fields are live-mutated with no
  // change event, so they must be re-read each frame.
  private bedRoute = new Map<string, BedRoute>();
  // A10 — last COMMANDED gain per bed handle (the target we last wrote, NOT the
  // live param value). The per-frame gain write is epsilon-gated against this so
  // a static bed writes 0 automation ops/frame; the cache updates ONLY on an
  // actual write, so a slow LFO still drifts it and keeps breathing.
  private bedGain = new Map<string, number>();
  // A10 — reused per-frame "active this tick" set (cleared, not re-allocated).
  private bedLive = new Set<string>();
  // A10 — instrumentation: bed gain writes committed since boot. Static beds →
  // flat; LFO/mid-morph beds → climbs. (Read via the getter.)
  private _bedGainWrites = 0;
  // A12 — per-bed drop schedule: nextFire audio-time per member + the bed's
  // last fire (for minSpacingSec interleaving). Seeded on activation, removed
  // on deactivation — pending fires die with the activation by construction.
  private dropState = new Map<string, { next: number[]; lastFire: number }>();
  // N1 — live clip regions: handle + where in the SOURCE it started and when
  // (audio clock), so each frame can compare expected vs actual source position
  // and re-seek on scrubs. Keyed by placement id.
  private clipHandles = new Map<string, { handle: string; startSrcPos: number; startedAtAudio: number }>();
  // narration-MVP — the last handle each TRIGGER clip started (timeline edge-fire
  // OR explicit fireClipExternally). Lets the external path stop a still-sounding
  // instance before replaying so a voice is never doubled (single-voice contract).
  // Trigger clips are fire-and-forget (no per-frame prune), so a stale id here is
  // harmless — stopHandle no-ops on a finished/unknown handle.
  private triggerHandles = new Map<string, string>();
  // N7 — clip ids currently DUCKING under a narration line (a ducking sync clip
  // while its region plays; a ducking trigger for its sound's length). While the
  // set is non-empty the music dips to N7_DUCK and the room (ambient) to
  // N7_DUCK_AMBIENT; both lift back when it empties.
  private duckers = new Set<string>();
  private music: { handle: string; userSelected: boolean; placementId?: string; assetId?: string; startedAt?: number; startOffset?: number } | null = null;
  private lastEra: StateName | null = null;
  // N4 — per-track position memory for resume:'continue' music placements:
  // assetId → seconds into the track when it last left the slot.
  private musicPos = new Map<string, number>();
  // N8: the era whose default music was deferred because it requires power —
  // the slot wakes it when the generator latches (no era edge needed).
  private musicPowerPending: StateName | null = null;
  // A4: the era whose default music is deferred because its sample is still
  // streaming in (boot no longer awaits every decode). The slot retries each
  // frame and wakes when the buffer lands — there's no era edge to retrigger it.
  private musicLoadPending: StateName | null = null;
  // A2: the era whose default music is deferred because its anchor hero isn't
  // in the scene yet (or is missing). The slot retries each frame and wakes if
  // the hero appears — without it, a not-yet-loaded boombox would strand the
  // era default silent until the next era edge.
  private musicAnchorPending: StateName | null = null;

  constructor(private deps: SoundscapeDeps) {}

  /** Call once per frame with the clock's t + running flag. */
  update(t: number, running: boolean): void {
    if (this.deps.audio.contextState !== 'running') { this.lastT = t; return; }
    const era = this.deps.currentEra();
    const morph = this.deps.morph();
    this.updateMusic(era);
    this.updateBeds(t, morph, running);
    this.updateClips(t, running, era); // N10: handles BOTH sync regions + trigger one-shots
  }

  // ── music slot (diegetic; era default + user override) ────────────────────
  private updateMusic(era: StateName): void {
    // A non-looping tape that finished → silent (matches eject).
    if (this.music && !this.deps.audio.isHandlePlaying(this.music.handle)) {
      // The track left the slot WITHOUT stopMusic (which nulls first) — it
      // finished on its own. For a visitor's tape that's beat 6 (E3): the
      // song is over, the past exhales back. No button did this; time did.
      const wasTape = this.music.userSelected;
      this.music = null;
      if (wasTape) this.deps.onTapeEnded?.();
    }
    if (era !== this.lastEra) {
      this.lastEra = era;
      // A user tape rides across era changes; only the default swaps.
      if (!this.music?.userSelected) this.playEraDefault(era);
    } else if (this.musicPowerPending === era && !this.music && (this.deps.powered?.() ?? true)) {
      // N8: power arrived mid-era — the deferred era default wakes now (there
      // is no era edge to retrigger it, so this is the slot's wake-on-power).
      this.musicPowerPending = null;
      this.playEraDefault(era);
    } else if (this.musicLoadPending === era && !this.music) {
      // A4: the era default's sample was still streaming — retry until its
      // buffer lands. playEraDefault re-checks isLoaded and either plays (and
      // clears the pending) or re-defers, so this is silent until ready.
      this.playEraDefault(era);
    } else if (this.musicAnchorPending === era && !this.music) {
      // A2: the era default's anchor hero wasn't in the scene yet — retry until
      // it appears (or stay silent + warned if it's genuinely missing).
      this.playEraDefault(era);
    }
  }
  private playEraDefault(era: StateName): void {
    this.stopMusic(0.5);
    if (!(this.deps.autoEraMusic?.() ?? true)) {
      // Demo (silence-until-cassette): the era's DEFAULT never auto-plays — a
      // visitor's music is the cassette they insert (setMusicOverride). Clear the
      // deferred-wake flags so no pending power/load/anchor retry sneaks the
      // default back in mid-era.
      this.musicPowerPending = this.musicLoadPending = this.musicAnchorPending = null;
      return;
    }
    const def = this.deps.getPlacements().find(
      (p) => p.kind === 'music' && (p.scope === era || p.scope === 'both'),
    );
    if (def?.requiresPower && !(this.deps.powered?.() ?? true)) {
      // N8: the era's default is electrical and the shop is dark — remember it
      // so the slot wakes when the generator latches (see updateMusic).
      this.musicPowerPending = era;
      return;
    }
    this.musicPowerPending = null;
    if (!def?.asset) return; // music always carries an asset (validator enforces)
    if (!this.deps.audio.isLoaded(def.asset)) {
      // A4: the sample is still streaming (boot no longer awaits every decode).
      // Defer without calling play() — that keeps the slot quiet (no
      // "[audio] not loaded" warn) and the updateMusic retry wakes it once the
      // buffer lands.
      this.musicLoadPending = era;
      return;
    }
    this.musicLoadPending = null;
    // N4 — resume policy: 'restart' (default — the old behavior, now CHOSEN)
    // starts at 0:00; 'continue' resumes where this track last left the slot
    // (wrapped into the track length, since era defaults loop).
    let offset = 0;
    if (def.resume === 'continue') {
      const remembered = this.musicPos.get(def.asset) ?? 0;
      const len = this.deps.audio.assetDuration(def.asset);
      offset = len && len > 0 ? remembered % len : remembered;
    }
    const at = this.resolveAt(def.at);
    if (at === UNRESOLVED) {
      // A2: the boombox (or whatever anchors this era's music) isn't resolvable
      // yet — defer + retry rather than play the track everywhere, unattenuated.
      this.musicAnchorPending = era;
      return;
    }
    this.musicAnchorPending = null;
    const h = this.deps.audio.play(def.asset, {
      channel: 'music', loop: true, volume: def.gain ?? 0.6, fadeIn: 0.6,
      at, spatial: def.spatial ?? 'area',
      refDistance: def.refDistance, maxDistance: def.maxDistance,
      offset,
    });
    if (h) this.music = { handle: h, userSelected: false, placementId: def.id, assetId: def.asset, startedAt: this.deps.audio.audioTime, startOffset: offset };
  }
  /** Cassette inserted — take over the music slot; rides across era changes. */
  setMusicOverride(assetId: string, at: PlayAt | undefined, volume = 0.9): void {
    if (!this.deps.audio.isLoaded(assetId)) {
      console.warn('[soundscape] override not loaded:', assetId);
      return;
    }
    this.stopMusic(0.05);
    // E3: a visitor's tape plays ONCE (loop:false) — the song lasting is the
    // point; its natural end returns the room to the present (onTapeEnded).
    // Era DEFAULTS keep looping (they're the room's weather, not a visit).
    const h = this.deps.audio.play(assetId, {
      channel: 'music', loop: false, volume, fadeIn: 0.3, at, spatial: 'area',
    });
    if (h) this.music = { handle: h, userSelected: true, assetId, startedAt: this.deps.audio.audioTime, startOffset: 0 };
  }
  /** Boombox eject — silence the music slot (no auto-resume until era change). */
  clearMusicOverride(): void {
    this.stopMusic(0.4);
  }
  private stopMusic(fade: number): void {
    if (this.music) {
      // N4: remember where the outgoing track was (cheap for every track;
      // only resume:'continue' placements ever read it back).
      if (this.music.assetId && this.music.startedAt !== undefined) {
        const pos = (this.music.startOffset ?? 0) + (this.deps.audio.audioTime - this.music.startedAt);
        this.musicPos.set(this.music.assetId, pos);
      }
      this.deps.audio.stopHandle(this.music.handle, fade);
      this.music = null;
    }
  }

  /** N4 — the slot's truth for the timeline's persistent music strip:
   *  occupant, live position (seconds into the track), whether a visitor tape
   *  is riding the slot, and what an era flip would swap to. Null when silent. */
  musicStatus(era: StateName): {
    assetId: string | null; posSec: number; userSelected: boolean;
    resume: 'restart' | 'continue'; nextOnFlip: string | null;
  } {
    const other: StateName = era === 'past' ? 'present' : 'past';
    const nextDef = this.deps.getPlacements().find((p) => p.kind === 'music' && (p.scope === other || p.scope === 'both'));
    const cur = this.music && this.deps.audio.isHandlePlaying(this.music.handle) ? this.music : null;
    const curDef = cur?.placementId ? this.deps.getPlacements().find((p) => p.id === cur.placementId) : undefined;
    return {
      assetId: cur?.assetId ?? null,
      posSec: cur && cur.startedAt !== undefined ? (cur.startOffset ?? 0) + (this.deps.audio.audioTime - cur.startedAt) : 0,
      userSelected: cur?.userSelected ?? false,
      resume: curDef?.resume ?? 'restart',
      // A riding visitor tape survives the flip — the default only swaps in
      // when the slot isn't user-held.
      nextOnFlip: cur?.userSelected ? (cur.assetId ?? null) : (nextDef?.asset ?? null),
    };
  }

  // ── beds (level-triggered + era crossfade; drop populations ride along) ────
  private updateBeds(t: number, morph: number, running: boolean): void {
    const live = this.bedLive; live.clear(); // A10: reuse, don't allocate per frame
    const now = this.deps.audio.audioTime;
    const powered = this.deps.powered?.() ?? true;
    for (const p of this.deps.getPlacements()) {
      if (p.kind !== 'bed') continue;
      // N8: electrical beds wait for the generator. Skipping BEFORE live.add
      // means an (impossible today) power loss would fade them out via the
      // prune below — and power arriving simply lets them start next frame.
      if (p.requiresPower && !powered) continue;
      // Per-bed runtime gate (sewing toggle): held-off beds skip before live.add,
      // so the prune below fades a playing one out; re-enabling restarts it next frame.
      if (this.deps.bedEnabled && !this.deps.bedEnabled(p.id)) continue;
      const w = eraWeight(p.scope, morph);
      if (w <= ERA_EPSILON || !inSpan(p.span, t)) continue;
      live.add(p.id); // active this frame (even if we then skip it)
      // A12: the bed's member population fires regardless of whether a base
      // loop exists (a bed may be a pure drop population).
      if (p.drops?.length) this.tickDrops(p, w, now, running);
      // A pure population has no base loop to manage.
      if (!p.asset && !p.vary?.oneOf?.length) continue;
      const gain = (p.gain ?? 0.4) * w * lfo(p.mod, now);
      const ch = p.channel ?? 'ambient';
      const handle = this.bedHandles.get(p.id);
      if (handle && this.deps.audio.isHandlePlaying(handle)) {
        // Drop the handle (→ restart next frame, fresh oneOf + detune) when the
        // reshuffle timer fires (loop-to-loop texture) OR the authored route
        // changed live — channel re-route or spatial-radius tuning. Crossfades.
        const rs = p.mod?.reshuffleSec;
        const started = this.bedStarted.get(p.id) ?? now;
        const reshuffleDue = rs != null && rs > 0 && now - started >= rs;
        if (reshuffleDue || this.bedRouteChanged(p, ch)) {
          this.deps.audio.stopHandle(handle, BED_FADE);
          this.bedHandles.delete(p.id);
          this.bedStarted.delete(p.id);
          this.bedGain.delete(p.id);
        } else if (now - started >= BED_FADE) {
          // A10: hold off until the authored BED_FADE fade-in has run — a
          // per-frame setHandleVolume otherwise cancels the in-flight 0.5s ramp
          // and collapses it to ~80ms from near-zero. After that, write only
          // when the target moved past EPS, caching the COMMANDED gain (not the
          // live param) so a slow LFO still drifts the cache and keeps breathing.
          const last = this.bedGain.get(p.id);
          if (last === undefined || Math.abs(gain - last) >= BED_GAIN_EPS) {
            this.deps.audio.setHandleVolume(handle, gain);
            this.bedGain.set(p.id, gain);
            this._bedGainWrites++;
          }
        }
      } else if (!this.bedSkipped.has(p.id)) {
        // First frame of this activation — roll variation once, then commit
        // (play or skip) until the bed next leaves its window.
        //
        // A11 (John's ruling 2026-06-10: "the bed itself always happens; only
        // its details vary"): the chance roll runs ONLY on a genuine activation
        // start. Re-entries within the same activation — reshuffle, live route
        // change, a non-loop variant ending naturally — used to re-roll here,
        // and a failed roll landed in bedSkipped, which never clears for
        // span-[0,1]/'both' beds: an absorbing lockout. bedRoute survives those
        // re-entries and clears on window exit, so it's the free "already
        // started this activation" discriminator. Bed-level `chance` is a
        // legacy knob headed for deprecation — per-fire randomness lives in
        // the drops system (A12).
        const v = p.vary;
        if (!this.bedRoute.has(p.id) && v?.chance != null && Math.random() > v.chance) {
          this.bedSkipped.add(p.id);
          continue;
        }
        const chosen = pickAsset(v, p.asset);
        // A4: the chosen variant (oneOf overrides `asset`) may still be
        // streaming in — boot no longer awaits every decode. Skip silently and
        // retry next frame (re-rolling oneOf), no "[audio] not loaded" storm.
        if (chosen && !this.deps.audio.isLoaded(chosen)) continue;
        const at = this.resolveAt(p.at);
        if (at === UNRESOLVED) continue; // A2: missing anchor — skip, retry next frame (warn-once)
        const h = this.deps.audio.play(chosen, {
          channel: ch, loop: p.loop ?? true, volume: gain,
          fadeIn: BED_FADE, at, spatial: p.spatial ?? 'point',
          refDistance: p.refDistance, maxDistance: p.maxDistance,
          detune: pickDetune(v),
        });
        if (h) {
          this.bedHandles.set(p.id, h);
          this.bedStarted.set(p.id, now);
          this.bedRoute.set(p.id, this.bedRouteOf(p, ch));
          this.bedGain.set(p.id, gain); // A10: seed the cache with the commanded gain
        }
      }
    }
    // Anything no longer active: stop its handle + clear its skip/started state
    // so the next time it enters the window the variation re-rolls. Drop
    // schedules die with the activation (A12: pending fires are cancelled).
    // A10: iterate the Maps/Set directly — deleting the current key during a
    // for-of is safe, and the prior [...spread] copy was pure per-frame garbage.
    for (const [id, h] of this.bedHandles) {
      if (!live.has(id)) {
        this.deps.audio.stopHandle(h, BED_FADE);
        this.bedHandles.delete(id);
        this.bedStarted.delete(id);
        this.bedRoute.delete(id);
        this.bedGain.delete(id);
      }
    }
    for (const id of this.dropState.keys()) {
      if (!live.has(id)) this.dropState.delete(id);
    }
    for (const id of this.bedSkipped) {
      if (!live.has(id)) this.bedSkipped.delete(id);
    }
  }

  /** A10 — the bed's current audible route (channel + spatial falloff), read
   *  fresh from the live placement (its fields are mutated without a change
   *  event). Stored on start; compared field-wise each frame by bedRouteChanged. */
  private bedRouteOf(p: SoundPlacement, ch: string): BedRoute {
    return { ch, spatial: p.spatial ?? 'point', ref: p.refDistance, max: p.maxDistance };
  }
  private bedRouteChanged(p: SoundPlacement, ch: string): boolean {
    const r = this.bedRoute.get(p.id);
    return !r || r.ch !== ch || r.spatial !== (p.spatial ?? 'point') || r.ref !== p.refDistance || r.max !== p.maxDistance;
  }

  /** A10 instrumentation: bed gain writes committed since boot. Static beds keep
   *  this flat frame-to-frame; LFO / mid-morph beds climb. */
  get bedGainWrites(): number {
    return this._bedGainWrites;
  }

  // ── drops (A12) — the bed's member population ──────────────────────────────
  // Scheduled on the ENGINE clock (audioTime read per frame tick), never
  // setTimeout: fires die with the activation and respect era + pause. Each
  // member keeps its own nextFire; a fire plays ONE one-shot at the bed's (or
  // the member's) anchor with per-fire gain/detune weather, then reschedules.
  private tickDrops(p: SoundPlacement, w: number, now: number, running: boolean): void {
    const drops = p.drops!;
    let st = this.dropState.get(p.id);
    if (!st) {
      // Activation start: stagger every member into its own window.
      st = { next: drops.map((d) => now + randRange(d.every)), lastFire: -Infinity };
      this.dropState.set(p.id, st);
    }
    for (let i = 0; i < drops.length; i++) {
      const d = drops[i];
      if (st.next[i] === undefined) st.next[i] = now + randRange(d.every); // member added live
      if (now < st.next[i]) continue;
      if (!running) {
        // Paused/scrubbing clock fires nothing — slide the overdue slot forward
        // so resume doesn't burst-fire everything that came due meanwhile.
        st.next[i] = now + randRange(d.every);
        continue;
      }
      st.next[i] = now + randRange(d.every); // consume the slot (even on a miss)
      if (d.chance != null && Math.random() > d.chance) continue;
      const spacing = p.minSpacingSec ?? 0;
      if (spacing > 0 && now - st.lastFire < spacing) {
        // Interleaving (BotW footstep principle): blocked members re-roll a
        // SHORT deferral instead of piling onto the same moment.
        st.next[i] = now + 0.5 + Math.random() * spacing;
        continue;
      }
      const asset = d.oneOf[Math.floor(Math.random() * d.oneOf.length)];
      const base = Array.isArray(d.gain) ? randRange(d.gain) : (d.gain ?? 0.7);
      const at = this.resolveAt(d.at ?? p.at);
      if (at === UNRESOLVED) continue; // A2: missing anchor — skip this fire (slot already consumed)
      const h = this.deps.audio.play(asset, {
        channel: p.channel ?? 'ambient',
        volume: base * w, // era weight scales the member, same as the bed base
        at,
        spatial: p.spatial ?? 'point',
        refDistance: p.refDistance,
        maxDistance: p.maxDistance,
        detune: d.detune && d.detune > 0 ? (Math.random() * 2 - 1) * d.detune : undefined,
      });
      if (h) {
        st.lastFire = now;
        devBus.emit('drop:fire', { placementId: p.id, asset });
      }
    }
  }

  // ── clips (N1 — timeline-aligned regions; beds are weather, cues are
  //    lightning, clips are speech) ─────────────────────────────────────────
  // A clip plays IFF the playhead sits inside its region [t, t+dur/dayLen),
  // the clock is RUNNING (pause stops it), its era gate passes (binary), and
  // the mapped source position is inside the source window [in, out]. The
  // source position is locked to the timeline: srcPos = (playheadT − t)·dayLen
  // + in — entering mid-region starts mid-clip, and a scrub that moves the
  // playhead re-seeks (drift > CLIP_RESYNC_EPS → restart at the new offset).
  private updateClips(t: number, running: boolean, era: StateName): void {
    // N10 — ONE citizen, two playback modes. `trigger` clips (the former cues)
    // edge-fire on a forward crossing of `t`; `sync` clips (default, N1) are
    // clock-locked regions. The edge detector that used to live in
    // updateOneShots now drives the trigger half here.
    const prev = this.lastT;
    this.lastT = t;
    let edge = prev >= 0 && running && t !== prev; // a real forward tick, not a pause/seek
    // W4 — the cross-pass re-arm: a backward step (the W3 dusk→dawn wrap, or any
    // t<prev) clears the fired-this-pass latch, so every timeline trigger fires
    // ONCE PER daylight loop. This MUST stay BEFORE the MAX_STEP edge-kill below:
    // the wrap is a big jump (>MAX_STEP) that kills `edge`, but the clear is
    // gated only on `t<prev`, so it survives the wrap. ⚠ A day run ONE-SHOT
    // (never wrapping) never clears — narration fires once by design; the
    // hands-off attract loop must wrap to re-arm.
    if (edge && t < prev) this.firedThisPass.clear();
    if (edge && ((t - prev) % 1 + 1) % 1 > MAX_STEP) edge = false; // a scrub/seek, don't machine-gun

    const dayLen = Math.max(1, this.deps.dayLengthSeconds());
    const now = this.deps.audio.audioTime;
    const shouldLive = new Set<string>();
    for (const p of this.deps.getPlacements()) {
      if (p.kind !== 'clip' || p.t === undefined) continue;
      if (p.playback === 'trigger') {
        // Edge-fired one-shot: fire once on crossing, then play out fire-and-
        // forget (a scrub-away never stops it — that's the trigger contract).
        // narration-MVP — the cross-source latch: skip the auto-fire if this
        // clip was already fired this pass. A visitor firing it explicitly
        // (fireClipExternally) pre-marks it, so the timeline's own crossing is
        // suppressed and the line never doubles. The mark clears on loop-wrap.
        if (edge && !this.firedThisPass.has(p.id) && (!p.scope || p.scope === 'both' || p.scope === era) && crossed(prev, t, p.t)) {
          this.firedThisPass.add(p.id); // consumed this pass (a chance-miss still consumed its crossing)
          this.fireTriggerClip(p);
        }
        continue;
      }
      // sync region (N1): needs a real-seconds length + a source.
      if (p.dur === undefined || !p.asset) continue;
      const inRegion = t >= p.t && t < p.t + p.dur / dayLen;
      const eraOk = !p.scope || p.scope === 'both' || p.scope === era;
      // Timeline-mapped source position (seconds into the source).
      const srcPos = (t - p.t) * dayLen + (p.in ?? 0);
      // The source window ends at `out` if trimmed, else at the decoded length
      // (samples) — synth assets have no fixed inside and run to region end.
      const srcEnd = p.out ?? this.deps.audio.assetDuration(p.asset) ?? Infinity;
      if (!(inRegion && eraOk && running && srcPos < srcEnd)) continue;
      const cur = this.clipHandles.get(p.id);
      if (cur && this.deps.audio.isHandlePlaying(cur.handle)) {
        // Drift check: where the source actually is vs where the timeline says
        // it should be. A scrub inside the region shows up here as drift.
        const actual = cur.startSrcPos + (now - cur.startedAtAudio);
        if (Math.abs(actual - srcPos) > CLIP_RESYNC_EPS) {
          this.deps.audio.stopHandle(cur.handle, CLIP_FADE);
          this.startClip(p, srcPos, now);
        }
      } else {
        // N7 — arbitrate against the visitor's tape before STARTING a region.
        const act = this.arbitrate(p);
        if (act === 'hold') continue; // skip/defer under a tape: don't start (not live; retries when it clears)
        this.startClip(p, srcPos, now);
        if (act === 'duck') this.setDuck(p.id, true);
      }
      shouldLive.add(p.id);
    }
    // Sync clips no longer live stop: left the region, paused, era-gated, or
    // trimmed out. (Trigger clips never register a handle here, so they're not
    // pruned — they play out on their own source.)
    for (const [id, c] of [...this.clipHandles]) {
      if (!shouldLive.has(id)) {
        const p = this.deps.getPlacements().find((x) => x.id === id);
        this.deps.audio.stopHandle(c.handle, p?.fadeOut ?? CLIP_FADE);
        this.clipHandles.delete(id);
        if (this.duckers.has(id)) this.setDuck(id, false); // N7: lift the duck when a ducking region ends
      }
    }
  }

  // ── N7 — script⇄performer arbitration ─────────────────────────────────────
  /** A visitor's cassette is riding the music slot (the canonical collision). */
  private hasVisitorTape(): boolean {
    return !!(this.music?.userSelected && this.deps.audio.isHandlePlaying(this.music.handle));
  }
  /** What a clip should do right now given a possible collision with the tape:
   *  'play' (force/unset, or skip/defer with nothing competing), 'duck' (play +
   *  dip music), 'hold' (skip/defer while the tape rides — don't play now). The
   *  defer window + per-clip default + eject-restore feel are John's calls. */
  private arbitrate(p: SoundPlacement): 'play' | 'duck' | 'hold' {
    const mode = p.arbitration;
    if (mode === 'duck') return 'duck';
    if ((mode === 'skip' || mode === 'defer') && this.hasVisitorTape()) return 'hold';
    return 'play';
  }
  private setDuck(id: string, on: boolean): void {
    if (on) this.duckers.add(id); else this.duckers.delete(id);
    const ducking = this.duckers.size > 0;
    // Dip BOTH the visitor's cassette (music) AND the room (ambient beds) under a
    // narration line, then lift both when the last ducker ends. Music alone left
    // the voice fighting the full room whenever no tape was playing (item 4).
    this.deps.audio.duckChannel('music', ducking ? N7_DUCK : 1);
    this.deps.audio.duckChannel('ambient', ducking ? N7_DUCK_AMBIENT : 1);
  }

  /** N10 — fire a `trigger` clip (the former cue): chance gate + optional jitter,
   *  then play its asset fire-and-forget. The old "a cue runs arbitrary actions"
   *  power is retired — a trigger plays ONE sound (its asset, with oneOf/detune),
   *  the only thing every cue in practice did. Honours the A2/A3/A4 guards. */
  //  narration-MVP — `force` is the explicit-intent path (fireClipExternally):
  //  a visitor firing the clip on purpose bypasses the arbitration HOLD and the
  //  chance gate ("explicit intent wins"). The timeline's own edge-fire calls
  //  this with force=false, so its automation still respects skip/defer + chance.
  private fireTriggerClip(p: SoundPlacement, force = false): void {
    const act = this.arbitrate(p);
    if (!force && act === 'hold') return; // N7: skip/defer under a visitor tape — don't fire (crossing already consumed)
    const v = p.vary;
    if (!force && v?.chance != null && Math.random() > v.chance) return;
    const go = (): void => {
      const asset = pickAsset(v, p.asset);
      if (!asset || !this.deps.audio.isLoaded(asset)) return; // missing/streaming → skip (A3/A4)
      const at = this.resolveAt(p.at);
      if (at === UNRESOLVED) return; // A2: missing anchor → skip, don't play globally
      // W1 — honor in/out so a trigger plays only its trimmed region (out caps
      // the audio, sample-accurate) with a fade on both ends. assetDuration is
      // the PICKED asset's true length (a oneOf variant may differ from p.asset);
      // synth ignores offset/duration (no inside to seek/cap).
      const inP = p.in ?? 0;
      const srcEnd = p.out ?? this.deps.audio.assetDuration(asset) ?? Infinity;
      const duration = Number.isFinite(srcEnd) ? Math.max(0, srcEnd - inP) : undefined;
      const h = this.deps.audio.play(asset, {
        channel: p.channel ?? 'sfx',
        volume: p.gain ?? 0.8,
        at,
        spatial: p.spatial ?? (p.at ? 'point' : 'global'),
        refDistance: p.refDistance,
        maxDistance: p.maxDistance,
        detune: pickDetune(v),
        offset: inP,
        duration,
        fadeIn: p.fadeIn ?? DEFAULT_FADE,
        fadeOut: p.fadeOut ?? DEFAULT_FADE,
        // Narration is MONOPHONIC: starting any narration clip fades out any other
        // narration voice first (cassettes-vs-digital vs why-sudanese, or a timeline
        // line vs a user-triggered one), so lines never overlap — one plays at a
        // time. Other channels (sfx/ambient/music) stay polyphonic.
        exclusive: p.channel === 'narration',
      });
      // narration-MVP — remember the voice so a later external fire (or loop
      // re-fire) can stop it first and never double the line.
      if (h) this.triggerHandles.set(p.id, h);
      // N7: a ducking trigger dips the music for the sound's ACTUAL length (the
      // capped duration when known, else the authored dur), then lifts.
      if (h && act === 'duck') {
        this.setDuck(p.id, true);
        setTimeout(() => this.setDuck(p.id, false), Math.max(0.3, duration ?? p.dur ?? 2) * 1000);
      }
      devBus.emit('cue:fire', { id: p.id }); // N5 fired-state (the marker/bar lights spent)
    };
    if (v?.jitterSec && v.jitterSec > 0) setTimeout(go, Math.random() * v.jitterSec * 1000);
    else go();
  }

  /** narration-MVP — the user-event play path for a trigger clip (a DM13
   *  bookmark's onArrive, a chair-trigger, any explicit fire). The other half
   *  of the auto/interactive duality: unlike the timeline's edge-fire this
   *  ALWAYS plays (force — no chance/defer gate suppresses it), stops any live
   *  instance of the SAME clip first so a voice never doubles (a timeline tail
   *  still sounding, or a prior external fire), and marks it fired-this-pass so
   *  the timeline's own crossing is suppressed for the rest of this loop. The
   *  latch clears on loop-wrap (firedThisPass.clear in updateClips), so the
   *  hands-off attract loop performs the line fresh every pass. */
  fireClipExternally(clipId: string): void {
    const p = this.deps.getPlacements().find((x) => x.id === clipId && x.kind === 'clip');
    if (!p) { console.warn('[soundscape] fireClipExternally: no clip', clipId); return; }
    // Single voice: stop the clip's currently-sounding instance (timeline tail
    // or an earlier external fire) before replaying — never two voices at once.
    const live = this.triggerHandles.get(clipId);
    if (live) { this.deps.audio.stopHandle(live, CLIP_FADE); this.triggerHandles.delete(clipId); }
    this.firedThisPass.add(clipId); // suppress the timeline's own crossing this pass
    this.fireTriggerClip(p, true);  // force: explicit intent bypasses chance/defer
  }

  private startClip(p: SoundPlacement, srcPos: number, now: number): void {
    const at = this.resolveAt(p.at);
    if (at === UNRESOLVED) return; // A2: clip anchored to a missing hero — skip, don't play globally
    // W1 — hard-cap the source at `out` (the remaining region length from the
    // current source position) with a tail fade, so a sync region stops at out
    // with a fade rather than relying only on the per-frame teardown. fadeIn
    // stays the small CLIP_FADE declick: a drift-resync restarts the source, and
    // an authored fade-in there would audibly pump on every scrub. Synth ignores
    // duration (no inside).
    const srcEnd = p.out ?? this.deps.audio.assetDuration(p.asset!) ?? Infinity;
    const duration = Number.isFinite(srcEnd) ? Math.max(0, srcEnd - srcPos) : undefined;
    const h = this.deps.audio.play(p.asset!, {
      channel: p.channel ?? 'narration',
      volume: p.gain ?? 1,
      fadeIn: p.fadeIn ?? CLIP_FADE,
      fadeOut: p.fadeOut ?? DEFAULT_FADE,
      at,
      spatial: p.spatial ?? (p.at ? 'point' : 'global'),
      refDistance: p.refDistance,
      maxDistance: p.maxDistance,
      offset: srcPos, // ignored for synth assets (generated, not seeked)
      duration,
    });
    if (h) this.clipHandles.set(p.id, { handle: h, startSrcPos: srcPos, startedAtAudio: now });
  }

  // ── N5 diagnostics — the instrument's "why aren't you sounding?" answers ──
  private firedThisPass = new Set<string>();
  /** Has this trigger clip's crossing been consumed in the current clock-pass?
   *  (Named cueFiredThisPass for back-compat; trigger clips are the former cues.) */
  cueFiredThisPass(id: string): boolean {
    return this.firedThisPass.has(id);
  }
  /** A bed's live state, for the lane badge: playing / chance-skipped (the
   *  activation roll failed — silent until the bed re-enters its window) /
   *  silent (off-era, off-window, or simply not started). */
  bedState(id: string): 'playing' | 'chance-skipped' | 'silent' {
    if (this.bedSkipped.has(id)) return 'chance-skipped';
    const h = this.bedHandles.get(id);
    if (h && this.deps.audio.isHandlePlaying(h)) return 'playing';
    return 'silent';
  }

  /** Handles the bed reconciler currently owns (U8). Stopping one of these
   *  from another surface is futile — the reconciler restarts it next frame
   *  with a re-rolled variant — and their gain is recomputed per frame, so a
   *  volume slider is overwritten too. The inspector uses this to scope its
   *  PLAYING NOW controls to non-engine handles. The music handle is NOT here:
   *  music rows behave correctly (stop = silence till the next era change). */
  bedOwnedHandles(): Set<string> {
    return new Set(this.bedHandles.values());
  }

  /** Live gain of a bed/music placement's currently-sounding handle, or null if
   *  it isn't sounding. Drives the audio-source viz — dim when silent, brighter
   *  (scaled by this gain) when audible. */
  activeGain(placementId: string): number | null {
    const h = this.bedHandles.get(placementId);
    if (h && this.deps.audio.isHandlePlaying(h)) return this.deps.audio.getHandleVolume(h) ?? 0;
    if (this.music?.placementId === placementId && this.deps.audio.isHandlePlaying(this.music.handle)) {
      return this.deps.audio.getHandleVolume(this.music.handle) ?? 0;
    }
    return null;
  }

  private resolveAt(at: SoundPlacement['at']): PlayAt | undefined | typeof UNRESOLVED {
    if (!at) return undefined; // no anchor authored → intentionally 'global'
    if (at.heroId) {
      const obj = this.deps.getObjectByHeroId(at.heroId);
      if (obj) return { object: obj };
      // A2: an authored hero anchor that can't be resolved (renamed/missing
      // hero). NEVER fall through to a global, unattenuated, room-filling loop —
      // warn once and signal skip, unless a fixed position is also authored.
      this.warnUnresolved(at.heroId);
      if (at.position) return { position: at.position };
      return UNRESOLVED;
    }
    if (at.position) return { position: at.position };
    return undefined;
  }

  private warnedUnresolved = new Set<string>();
  private warnUnresolved(heroId: string): void {
    if (this.warnedUnresolved.has(heroId)) return;
    this.warnedUnresolved.add(heroId);
    console.warn('[soundscape] unresolved anchor heroId:', heroId, '— placement skipped (not played globally)');
  }
}

function inSpan(span: [number, number] | undefined, t: number): boolean {
  if (!span) return true;
  const [a, b] = span;
  return a <= b ? (t >= a && t <= b) : (t >= a || t <= b);
}

function crossed(prev: number, t: number, c: number): boolean {
  if (prev <= t) return c > prev && c <= t;
  return c > prev || c <= t;
}

// ── variation helpers (browser runtime — Math.random is fine here) ──────────
/** Pick from the oneOf pool if present, else the placement's base asset.
 *  (asset is optional since A12; callers guard that at least one exists.) */
function pickAsset(v: Variation | undefined, fallback: string | undefined): string {
  if (v?.oneOf && v.oneOf.length > 0) return v.oneOf[Math.floor(Math.random() * v.oneOf.length)];
  return fallback ?? '';
}

/** Uniform random in [min, max]. */
function randRange(r: [number, number]): number {
  return r[0] + Math.random() * (r[1] - r[0]);
}
/** A random detune in [-v.detune, +v.detune] cents, or undefined if unset. */
function pickDetune(v: Variation | undefined): number | undefined {
  if (v?.detune && v.detune > 0) return (Math.random() * 2 - 1) * v.detune;
  return undefined;
}
/** Gain LFO factor in [1-depth, 1] — a slow sine "breath" on a held bed. */
function lfo(mod: Mod | undefined, now: number): number {
  const depth = Math.min(1, mod?.depth ?? 0);
  if (depth <= 0) return 1;
  const rate = mod?.rateSec && mod.rateSec > 0 ? mod.rateSec : 6;
  return 1 - 0.5 * depth + 0.5 * depth * Math.sin((now / rate) * Math.PI * 2);
}
